"use client"

import { useEffect, useRef, useState } from "react"

const LG_BREAKPOINT = 1024

export default function MobileScrollHeader({
  children
}: {
  children: React.ReactNode
}) {
  const [isMobile, setIsMobile] = useState(false)
  const [isScrollHidden, setIsScrollHidden] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)

  // Detect mobile vs desktop
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${LG_BREAKPOINT - 1}px)`)

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches)
      if (!e.matches) {
        setIsScrollHidden(false)
        document.documentElement.style.setProperty(
          "--page-header-height",
          "0px"
        )
      }
    }

    handleChange(mql)
    mql.addEventListener("change", handleChange)
    return () => mql.removeEventListener("change", handleChange)
  }, [])

  // Scroll behavior - hide on scroll down, show on scroll up (mobile only)
  useEffect(() => {
    if (!isMobile) return

    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY
          const scrollThreshold = 80
          const direction = lastScrollY.current > currentScrollY ? "up" : "down"

          if (currentScrollY > scrollThreshold && direction === "down") {
            setIsScrollHidden(true)
          } else {
            setIsScrollHidden(false)
          }

          lastScrollY.current = currentScrollY
          ticking = false
        })

        ticking = true
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isMobile])

  // Update CSS variable for layout padding (keep constant regardless of scroll-hide
  // so the page content doesn't jump when the header slides away via transform)
  useEffect(() => {
    const updateHeight = () => {
      if (isMobile && headerRef.current) {
        const height = headerRef.current.offsetHeight
        document.documentElement.style.setProperty(
          "--page-header-height",
          `${height}px`
        )
      } else {
        document.documentElement.style.setProperty(
          "--page-header-height",
          "0px"
        )
      }
    }

    updateHeight()

    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(updateHeight, 100)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [isMobile])

  // On desktop, render children as-is with no wrapper styling
  if (!isMobile) {
    return <>{children}</>
  }

  // On mobile, render with fixed positioning and scroll behavior
  return (
    <div
      ref={headerRef}
      className={`
        fixed left-0 right-0 z-50 w-full
        transition-transform duration-300
        ${isScrollHidden ? "-translate-y-[calc(100%+var(--notification-banner-height,0px))]" : "translate-y-0"}
      `}
      style={{
        top: "var(--notification-banner-height, 0px)",
        transitionTimingFunction: "cubic-bezier(0.3, 1, 0.33, 1)"
      }}>
      {children}
    </div>
  )
}
